/**
 * A tab open across a deploy still runs the old bundle. Vercel's skew
 * protection serves that bundle's lazy chunks for 12 hours (the project's
 * `skewProtectionMaxAge`); after that they 404 and the next lazy route or
 * panel (the file preview, say) fails with "error loading dynamically
 * imported module". Vite reports each failed chunk as `vite:preloadError`;
 * reloading picks up the current deployment, whose chunks exist.
 *
 * One reload per window: a chunk that is missing from the fresh page too is
 * a real failure, so it is left to the error boundary rather than reloading
 * forever.
 */

export const STALE_CHUNK_RELOAD_KEY = "amoreg:stale-chunk-reload-at";
export const STALE_CHUNK_RELOAD_WINDOW_MS = 60_000;

/** Each engine words a failed module fetch differently. */
const STALE_CHUNK_MESSAGES = [
  "Failed to fetch dynamically imported module", // Chromium
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari
];

export function isStaleChunkMessage(message: string): boolean {
  return STALE_CHUNK_MESSAGES.some((fragment) => message.includes(fragment));
}

export function isStaleChunkError(error: unknown): boolean {
  return error instanceof Error && isStaleChunkMessage(error.message);
}

export interface StaleChunkReloadEnv {
  readonly storage: Pick<Storage, "getItem" | "setItem"> | null;
  readonly now: () => number;
  readonly reload: () => void;
}

/** Reloads once for a failed chunk; returns whether it did. */
export function reloadForStaleChunk(env: StaleChunkReloadEnv): boolean {
  // Without storage there is no record of the last reload, and a reload
  // could loop, so the error is left to the boundary.
  if (env.storage === null) return false;
  const now = env.now();
  try {
    const last = Number(env.storage.getItem(STALE_CHUNK_RELOAD_KEY));
    // A record from the future (the clock moved back) does not block a reload.
    const elapsed = now - last;
    if (last > 0 && elapsed >= 0 && elapsed < STALE_CHUNK_RELOAD_WINDOW_MS) return false;
    env.storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  env.reload();
  return true;
}

export function installStaleChunkReload(target: Window = window): void {
  const reloadOnce = () => {
    // Offline, the chunk failed for want of a network, not a deploy, and a
    // reload would swap the page and its unsaved work for the browser's
    // offline page. The error boundary keeps the page.
    if (target.navigator?.onLine === false) return;
    let storage: Storage | null = null;
    try {
      storage = target.sessionStorage;
    } catch {
      storage = null;
    }
    reloadForStaleChunk({
      storage,
      now: () => Date.now(),
      reload: () => target.location.reload(),
    });
  };
  // The event is not cancelled: a cancelled import resolves to undefined, and
  // React.lazy would report that as a confusing render error of its own in
  // the moment before the page goes.
  target.addEventListener("vite:preloadError", reloadOnce);
  // A dynamic import inside a dependency (a highlighter theme, say) may not
  // pass through Vite's preload helper, so it never raises the event above
  // and surfaces only as an unhandled rejection (AMOREG-EVE-B). The shared
  // window keeps the two paths to one reload.
  target.addEventListener("unhandledrejection", (event) => {
    if (isStaleChunkError(event.reason)) reloadOnce();
  });
}
